import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "../../../utils/translation";
import {
  listBusinesses,
  selectBusinesses,
  selectBusinessesError,
  selectBusinessesLoading,
  selectBusinessesMessage,
  updateBusinessUsers,
} from "../businessSlice";
import {
  listUsers,
  selectUsers,
  selectUsersError,
  selectUsersLoading,
  selectUsersMessage,
  updateUserBusinessId,
} from "../../user/userSlice";

const PAGE_SIZE = 10;

const BusinessAssignUsersPage = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const businesses = useSelector(selectBusinesses);
  const adminAccess = useSelector((state) => state.auth?.adminAccess);
  const users = useSelector(selectUsers);
  const bizLoading = useSelector(selectBusinessesLoading);
  const bizError = useSelector(selectBusinessesError);
  const bizMessage = useSelector(selectBusinessesMessage);
  const userLoading = useSelector(selectUsersLoading);
  const userError = useSelector(selectUsersError);
  const userMessage = useSelector(selectUsersMessage);
  const [businessSearch, setBusinessSearch] = useState("");
  const [page, setPage] = useState(1);
  const [assigning, setAssigning] = useState({});
  const [permissionError, setPermissionError] = useState("");

  useEffect(() => {
    dispatch(listBusinesses());
    dispatch(listUsers());
  }, [dispatch]);

  const highlightMatch = (text, term) => {
    if (!term) return text;
    const safeText = text || "";
    const lower = safeText.toLowerCase();
    const lowerTerm = term.toLowerCase();
    const index = lower.indexOf(lowerTerm);
    if (index === -1) return safeText || "-";
    const before = safeText.slice(0, index);
    const match = safeText.slice(index, index + term.length);
    const after = safeText.slice(index + term.length);
    return (
      <>
        {before}
        <span style={{ backgroundColor: "yellow" }}>{match}</span>
        {after}
      </>
    );
  };

  const getUserName = (user) => {
    if (user?.fullName) return user.fullName;
    const parts = [user?.name, user?.surName].filter(Boolean);
    return parts.length ? parts.join(" ") : "-";
  };

  const filteredBusinesses = useMemo(() => {
    const term = businessSearch.trim().toLowerCase();
    if (!term) return businesses || [];
    return (businesses || []).filter((biz) =>
      (biz?.businessName || "").toLowerCase().includes(term)
    );
  }, [businesses, businessSearch]);


  const filteredBusinessesPaged = useMemo(() => {
    const term = businessSearch.trim().toLowerCase();
    let result = businesses || [];
    if (term) {
      result = result.filter((b) => (b.businessName || "").toLowerCase().includes(term));
    }
    return result;
  }, [businesses, businessSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredBusinessesPaged.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedBusinesses = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredBusinessesPaged.slice(start, start + PAGE_SIZE);
  }, [filteredBusinessesPaged, currentPage]);

  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);

  const handleAssign = async (biz, userId) => {
    if (!adminAccess) {
      setPermissionError(t("admin_common.permission_denied"));
      return;
    }
    setPermissionError("");
    const bizId = String(biz.id || biz.businessId);
    const currentUsers = Array.isArray(biz.users) ? biz.users.map(String) : [];
    const currentPrimary = currentUsers[0] || "";
    if (currentPrimary && currentPrimary !== String(userId || "")) {
      const confirmed = window.confirm(t("admin_business_pages.assign_confirm_replace"));
      if (!confirmed) return;
    }
    const nextUserId = String(userId || "");
    await dispatch(updateBusinessUsers({ id: bizId, users: nextUserId ? [nextUserId] : [] }));
    if (nextUserId) {
      await dispatch(updateUserBusinessId({ id: nextUserId, businessId: bizId }));
    }
    if (currentPrimary && currentPrimary !== nextUserId) {
      await dispatch(updateUserBusinessId({ id: currentPrimary, businessId: "" }));
    }
    dispatch(listBusinesses());
    dispatch(listUsers());
  };

  const loading = bizLoading || userLoading;
  const error = bizError || userError;
  const errorMessage = bizMessage || userMessage;

  return (
    <div className="row">
      <div className="col-12">
        <div className="card">
          <div className="card-body">
            <h4 className="card-title">{t("admin_business_pages.assign_title")}</h4>
            {permissionError && (
              <div className="alert alert-danger">{permissionError}</div>
            )}
            {error && (
              <div className="text-danger mb-2">
                {errorMessage || t("admin_common.error_generic")}
              </div>
            )}
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="text-black font-w500">{t("admin_common.search_business_placeholder")}</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder={t("admin_common.search_business_placeholder")}
                  value={businessSearch}
                  onChange={(e) => {
                    setBusinessSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
            </div>

            <div className="table-responsive">
              <table className="table table-striped">
                <thead>
                  <tr>
                    <th>{t("admin_common.business")}</th>
                    <th>{t("admin_common.email")}</th>
                    <th>{t("admin_common.current_user")}</th>
                    <th>{t("admin_common.assign_user")}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && (
                    <tr>
                      <td colSpan="4">{t("admin_common.loading")}</td>
                    </tr>
                  )}
                  {!loading && pagedBusinesses.length === 0 && (
                    <tr>
                      <td colSpan="4">{t("admin_common.no_records")}</td>
                    </tr>
                  )}
                  {!loading && pagedBusinesses.map((biz) => {
                    const bizId = biz.id || biz.businessId;
                    const currentUserId = Array.isArray(biz.users) ? biz.users[0] : "";
                    const currentUser = users.find((u) => String(u.id || u.userId) === String(currentUserId));
                    const currentUserName = currentUser ? getUserName(currentUser) : "-";
                    const currentUserEmail = currentUser?.email || currentUser?.userEmail || "-";
                    return (
                      <tr key={bizId}>
                        <td>{highlightMatch(biz.businessName || "-", businessSearch.trim())}</td>
                        <td>{biz.businessMail || "-"}</td>
                        <td>{currentUserName}</td>
                        <td>
                          <select
                            className="form-select"
                            value={assigning[bizId] ?? currentUserId ?? ""}
                            onChange={(e) => {
                              const value = e.target.value;
                              setAssigning((prev) => ({ ...prev, [bizId]: value }));
                              handleAssign(biz, value);
                            }}
                          >
                            <option value="">{t("admin_common.select")}</option>
                            {(users || []).map((u) => {
                              const uid = u.id || u.userId;
                              return (
                                <option key={uid} value={uid}>
                                  {getUserName(u)} ({u.email || u.userEmail || "-"})
                                </option>
                              );
                            })}
                          </select>
                          {currentUserEmail !== "-" && (
                            <div className="text-muted small mt-1">{currentUserEmail}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="d-flex align-items-center justify-content-between mt-3">
              <span className="text-muted">
                {t("admin_common.total_records", { count: filteredBusinessesPaged.length })}
              </span>
              <div className="btn-group">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  {t("admin_common.prev")}
                </button>
                <button type="button" className="btn btn-outline-secondary" disabled>
                  {currentPage} / {totalPages}
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  {t("admin_common.next")}
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessAssignUsersPage;
